import { styled } from "goober";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { queries } from "../../shared/queries";
import type {
  BodyRef,
  HeaderPair,
  RequestLogRecord,
} from "../../shared/requestLogRecord";
import { useQuery } from "../hooks/useQuery";
import { executionContentPath } from "../lib/apiPaths";
import {
  decodeBodyBytes,
  decodeRequestBody,
  type DecodedRequestBody,
} from "../lib/decodeRequestBody";
import { apiUrl } from "../lib/tenantOrigin";
import { makeUrl, routes } from "../lib/urls";
import { CodeBlock } from "./CodeBlock";
import { ScrollContainer, Table, Th, Thead } from "./CompactTable";
import { ConnectorLogo } from "./ConnectorLogo";
import { Icon } from "./Icon";
import { Link } from "./Link";
import { PressButton } from "./PressButton";
import { StatusBadge } from "./StatusBadge";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

type ParsedLine =
  | { kind: "request"; data: RequestLogRecord }
  | { kind: "dropped"; count: number }
  | { kind: "raw"; raw: string };

function parseLines(content: string): ParsedLine[] {
  const out: ParsedLine[] = [];
  for (const raw of content.split("\n")) {
    if (!raw.trim()) continue;
    try {
      const obj = JSON.parse(raw);
      if (typeof obj.dropped === "number") {
        out.push({ kind: "dropped", count: obj.dropped });
      } else if (
        typeof obj.method === "string" &&
        typeof obj.url === "string"
      ) {
        out.push({ kind: "request", data: obj as RequestLogRecord });
      } else {
        out.push({ kind: "raw", raw });
      }
    } catch {
      out.push({ kind: "raw", raw });
    }
  }
  return out;
}

function statusColor(status: number | null): string {
  if (status == null || status >= 500) return "var(--red-800)";
  if (status >= 400) return "var(--amber-800)";
  if (status >= 300) return "var(--blue-800)";
  return "var(--green-800)";
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms)) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function formatHeaders(headers: HeaderPair[] | undefined): string {
  if (!headers || headers.length === 0) return "(no headers)";
  return headers.map(([name, value]) => `${name}: ${value}`).join("\n");
}

/**
 * Exception-only badges: recording is on for every normal run, so a fully
 * recorded exchange is the unbadged default and blob rows with no rec fields
 * stay unbadged too.
 */
function requestBadge(
  r: RequestLogRecord
): { label: string; color: "teal" | "orange"; tooltip: string } | null {
  if (r.replayed) {
    return {
      label: "Replayed",
      color: "teal",
      tooltip:
        "Served from a recorded step-test fixture instead of the network.",
    };
  }
  if (
    r.rec === 1 &&
    (r.req_body_truncated ||
      r.res_body_truncated ||
      r.res_body_complete === false)
  ) {
    return {
      label: "Partial",
      color: "orange",
      tooltip: "Body capture was truncated or the response didn’t complete.",
    };
  }
  if (r.rec_skipped === "budget") {
    return {
      label: "Not recorded",
      color: "orange",
      tooltip: "Recording stopped at the per-run budget before this request.",
    };
  }
  return null;
}

const Tr = styled("tr")`
  &[data-expandable="true"] > td:hover {
    background: var(--alpha4);
  }
  &[data-expanded="true"] > td {
    background: var(--alpha4);
  }
`;

const TrailingTd = styled("td")`
  border-bottom: 0.5px solid var(--border-alpha);
  height: 36px;
  padding: 0 16px 0 8px;
  vertical-align: middle;
`;

const TrailingInner = styled("div")`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

const ConnectorTrigger = styled(Link)`
  display: inline-flex;
`;

const RawCell = styled("td")`
  border-bottom: 0.5px solid var(--border-alpha);
  height: 36px;
  padding: 4px 16px;
  vertical-align: middle;
  font-family: var(--font-mono);
  font-size: 11px;
  line-height: 16px;
  color: var(--text-secondary);
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;

const ChevronTd = styled("td")`
  border-bottom: 0.5px solid var(--border-alpha);
  height: 36px;
  vertical-align: middle;
  padding-left: 8px;
  color: var(--text-secondary);
`;

const ChevronButton = styled(PressButton)`
  display: inline-flex;
  align-items: center;
  color: var(--text-secondary);
`;

// A data cell whose whole surface toggles the row's detail. The chevron
// remains the single keyboard/AT control (these carry tabIndex={-1}); leaving
// the trailing cell out keeps the connector Link from nesting inside a button.
const PressTd = styled("td")`
  border-bottom: 0.5px solid var(--border-alpha);
  border-right: 0.5px solid var(--border-alpha);
  height: 36px;
  padding: 0;
`;

const CellButton = styled(PressButton)`
  display: flex;
  align-items: center;
  width: 100%;
  height: 36px;
  min-width: 0;
  padding: 0 16px;
  cursor: pointer;
`;

const StaticCell = styled("div")`
  display: flex;
  align-items: center;
  width: 100%;
  height: 36px;
  min-width: 0;
  padding: 0 16px;
`;

// The chevron and trailing body cells draw no column dividers, so their
// header cells must not either, or the header looks like it has extra cells.
const BareTh = styled(Th)`
  border-right: none;
`;

const DetailTd = styled("td")`
  border-bottom: 0.5px solid var(--border-alpha);
  padding: 12px 16px;
  background: var(--alpha4);
  cursor: default;
`;

const DetailGrid = styled("div")`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const DetailSection = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const DetailNote = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
`;

const BinaryPlaceholder = styled("div")`
  padding: 8px 12px;
  border: 0.5px solid var(--border-alpha);
  border-radius: 8px;
  background: var(--alpha4);
  width: fit-content;
`;

function DecodedBody({ decoded }: { decoded: DecodedRequestBody }) {
  if (decoded.kind === "empty") {
    return (
      <Text variant="text-small" color="var(--text-secondary)">
        (empty body)
      </Text>
    );
  }
  if (decoded.kind === "binary") {
    return (
      <BinaryPlaceholder>
        <Text variant="text-small" color="var(--text-secondary)">
          binary, {decoded.byteLength.toLocaleString()} bytes
        </Text>
      </BinaryPlaceholder>
    );
  }
  return <CodeBlock content={decoded.text} wrap />;
}

/**
 * A recorded body: older records carry it inline as base64; newer ones hold
 * an `[offset, length]` ref into the run's request-bodies blob, fetched with
 * a Range request only when the row is opened.
 */
function BodyBlock({
  executionId,
  base64,
  bodyRef,
  headers,
  cache,
}: {
  executionId: string | undefined;
  base64: string | undefined;
  bodyRef: BodyRef | undefined;
  headers: HeaderPair[] | undefined;
  cache: Map<string, DecodedRequestBody>;
}) {
  const externalApiUrl = apiUrl();
  const inline = useMemo(
    () => (bodyRef ? null : decodeRequestBody(base64, headers ?? [])),
    [bodyRef, base64, headers]
  );

  // Primitive deps: a re-fetched requests blob re-parses into fresh record
  // objects, and identity deps would refetch every expanded body for nothing.
  // The headers ride a ref for the same reason — for a given ref they don't
  // change.
  const [offset, length] = bodyRef ?? [-1, -1];
  const cacheKey =
    executionId != null ? `${executionId}:${offset}:${length}` : null;
  // Read the cache in the initializer (not an effect) so a body already
  // fetched on a prior expand of this row renders immediately on remount,
  // with no fetch and no "Loading…" flash.
  const [fetched, setFetched] = useState<
    | { kind: "loading" }
    | { kind: "failed" }
    | { kind: "done"; decoded: DecodedRequestBody }
  >(() => {
    const cached = cacheKey ? cache.get(cacheKey) : undefined;
    return cached ? { kind: "done", decoded: cached } : { kind: "loading" };
  });
  const headersRef = useRef(headers);
  headersRef.current = headers;
  useEffect(() => {
    if (offset < 0 || !executionId) return;
    if (length === 0) {
      setFetched({ kind: "done", decoded: { kind: "empty" } });
      return;
    }
    const cached = cacheKey ? cache.get(cacheKey) : undefined;
    if (cached) {
      setFetched({ kind: "done", decoded: cached });
      return;
    }
    const controller = new AbortController();
    setFetched({ kind: "loading" });
    void fetch(
      `${externalApiUrl}${executionContentPath(executionId, "request-bodies")}`,
      {
        credentials: "include",
        headers: { Range: `bytes=${offset}-${offset + length - 1}` },
        signal: controller.signal,
      }
    )
      .then(async (res) => {
        if (res.status !== 206) throw new Error(String(res.status));
        const bytes = new Uint8Array(await res.arrayBuffer());
        if (!controller.signal.aborted) {
          const decoded = decodeBodyBytes(bytes, headersRef.current ?? []);
          if (cacheKey) cache.set(cacheKey, decoded);
          setFetched({ kind: "done", decoded });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setFetched({ kind: "failed" });
      });
    return () => controller.abort();
  }, [executionId, externalApiUrl, offset, length, cacheKey, cache]);

  if (inline) return <DecodedBody decoded={inline} />;
  if (fetched.kind === "loading") {
    return (
      <Text variant="text-small" color="var(--text-secondary)">
        Loading…
      </Text>
    );
  }
  if (fetched.kind === "failed") {
    return (
      <Text variant="text-small" color="var(--text-secondary)">
        Couldn’t load the recorded body.
      </Text>
    );
  }
  return <DecodedBody decoded={fetched.decoded} />;
}

function RequestDetail({
  data,
  executionId,
  cache,
}: {
  data: RequestLogRecord;
  executionId: string | undefined;
  cache: Map<string, DecodedRequestBody>;
}) {
  const truncated = data.req_body_truncated || data.res_body_truncated;
  const incomplete = data.res_body_complete === false;
  return (
    <DetailSection style={{ gap: 12 }}>
      {(truncated || incomplete) && (
        <DetailSection style={{ gap: 4 }}>
          {truncated && (
            <DetailNote>
              <Icon name="info" size={12} color="var(--orange-800)" />
              <Text variant="text-small" color="var(--orange-800)">
                Truncated at the 128 KiB recording cap
              </Text>
            </DetailNote>
          )}
          {incomplete && (
            <DetailNote>
              <Icon name="info" size={12} color="var(--orange-800)" />
              <Text variant="text-small" color="var(--orange-800)">
                Response did not complete
              </Text>
            </DetailNote>
          )}
        </DetailSection>
      )}
      <DetailGrid>
        <DetailSection>
          <Text variant="text-smaller-md" color="var(--text-secondary)">
            Request headers
          </Text>
          <CodeBlock content={formatHeaders(data.req_headers)} wrap />
          <Text variant="text-smaller-md" color="var(--text-secondary)">
            Request body
          </Text>
          <BodyBlock
            executionId={executionId}
            base64={data.req_body}
            bodyRef={data.req_body_ref}
            headers={data.req_headers}
            cache={cache}
          />
        </DetailSection>
        <DetailSection>
          <Text variant="text-smaller-md" color="var(--text-secondary)">
            Response headers
          </Text>
          <CodeBlock content={formatHeaders(data.res_headers)} wrap />
          <Text variant="text-smaller-md" color="var(--text-secondary)">
            Response body
          </Text>
          <BodyBlock
            executionId={executionId}
            base64={data.res_body}
            bodyRef={data.res_body_ref}
            headers={data.res_headers}
            cache={cache}
          />
        </DetailSection>
      </DetailGrid>
    </DetailSection>
  );
}

function RowCell({
  expandable,
  isExpanded,
  onPress,
  children,
}: {
  expandable: boolean;
  isExpanded: boolean;
  onPress: () => void;
  children: React.ReactNode;
}) {
  return (
    <PressTd>
      {expandable ? (
        <CellButton onPress={onPress} tabIndex={-1} aria-expanded={isExpanded}>
          {children}
        </CellButton>
      ) : (
        <StaticCell>{children}</StaticCell>
      )}
    </PressTd>
  );
}

export function RequestsList({
  content,
  executionId,
  bottomRef,
}: {
  content: string;
  executionId?: string;
  bottomRef?: React.Ref<HTMLDivElement>;
}) {
  const [apps] = useQuery(queries.connectedAppsLite());
  const appIdByConnector = useMemo(() => {
    const map = new Map<string, string>();
    for (const app of apps ?? []) {
      if (!map.has(app.identifier)) map.set(app.identifier, app.id);
    }
    return map;
  }, [apps]);
  // Bodies are immutable once recorded, so a fetched body survives
  // collapsing and re-expanding the same row instead of refetching.
  const bodyCache = useRef(new Map<string, DecodedRequestBody>()).current;
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
  const lines = useMemo(() => parseLines(content), [content]);

  function toggleExpanded(i: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) {
        next.delete(i);
      } else {
        next.add(i);
      }
      return next;
    });
  }

  return (
    // Bleed past StepDetails' 12px Body padding so the table sits flush with the panel edges.
    <ScrollContainer
      style={{
        margin: -12,
        width: "calc(100% + 24px)",
        height: "calc(100% + 24px)",
      }}
    >
      <Table style={{ tableLayout: "fixed", width: "100%", minWidth: 600 }}>
        <colgroup>
          <col style={{ width: 20 }} />
          <col style={{ width: 88 }} />
          <col />
          <col style={{ width: 72 }} />
          <col style={{ width: 84 }} />
          <col style={{ width: 112 }} />
        </colgroup>
        <Thead>
          <tr>
            <BareTh />
            <Th>
              <Text variant="text-small" color="var(--text-secondary)">
                Method
              </Text>
            </Th>
            <Th>
              <Text variant="text-small" color="var(--text-secondary)">
                URL
              </Text>
            </Th>
            <Th>
              <Text variant="text-small" color="var(--text-secondary)">
                Status
              </Text>
            </Th>
            <Th>
              <Text variant="text-small" color="var(--text-secondary)">
                Duration
              </Text>
            </Th>
            <BareTh />
          </tr>
        </Thead>
        <tbody>
          {lines.map((line, i) => {
            if (line.kind === "raw") {
              return (
                // oxlint-disable-next-line react/no-array-index-key
                <tr key={i}>
                  <RawCell colSpan={6} title={line.raw}>
                    {line.raw}
                  </RawCell>
                </tr>
              );
            }
            if (line.kind === "dropped") {
              return (
                // oxlint-disable-next-line react/no-array-index-key
                <tr key={i}>
                  <RawCell colSpan={6}>
                    … {line.count} record{line.count === 1 ? "" : "s"} dropped
                    (buffer full)
                  </RawCell>
                </tr>
              );
            }
            const r = line.data;
            const authTooltip = r.auth_connector
              ? `Credentials injected by the ${r.auth_connector} connector`
              : "Connector credentials injected";
            const connectorAppId = r.auth_connector
              ? appIdByConnector.get(r.auth_connector)
              : undefined;
            const badge = requestBadge(r);
            const expandable = r.rec === 1;
            const isExpanded = expandable && expanded.has(i);
            return (
              // oxlint-disable-next-line react/no-array-index-key
              <Fragment key={i}>
                <Tr
                  data-expandable={expandable || undefined}
                  data-expanded={isExpanded || undefined}
                >
                  <ChevronTd>
                    {expandable && (
                      <ChevronButton
                        onPress={() => toggleExpanded(i)}
                        aria-expanded={isExpanded}
                        aria-label={
                          isExpanded
                            ? "Collapse request details"
                            : "Expand request details"
                        }
                      >
                        <Icon
                          name={isExpanded ? "chevron-down" : "chevron-right"}
                          size={12}
                        />
                      </ChevronButton>
                    )}
                  </ChevronTd>
                  <RowCell
                    expandable={expandable}
                    isExpanded={isExpanded}
                    onPress={() => toggleExpanded(i)}
                  >
                    <Text variant="code-small" color="var(--text-secondary)">
                      {r.method}
                    </Text>
                  </RowCell>
                  <RowCell
                    expandable={expandable}
                    isExpanded={isExpanded}
                    onPress={() => toggleExpanded(i)}
                  >
                    <Text variant="code-small" ellipsis>
                      {r.url}
                    </Text>
                  </RowCell>
                  <RowCell
                    expandable={expandable}
                    isExpanded={isExpanded}
                    onPress={() => toggleExpanded(i)}
                  >
                    <Tooltip content={r.error} hidden={!r.error}>
                      <Text variant="code-small" color={statusColor(r.status)}>
                        {r.status ?? "ERR"}
                      </Text>
                    </Tooltip>
                  </RowCell>
                  <RowCell
                    expandable={expandable}
                    isExpanded={isExpanded}
                    onPress={() => toggleExpanded(i)}
                  >
                    <Text variant="code-small" color="var(--text-secondary)">
                      {formatDuration(r.duration_ms)}
                    </Text>
                  </RowCell>
                  <TrailingTd>
                    <TrailingInner>
                      {badge && (
                        <Tooltip content={badge.tooltip}>
                          <StatusBadge color={badge.color}>
                            {badge.label}
                          </StatusBadge>
                        </Tooltip>
                      )}
                      {r.auth_connector ? (
                        connectorAppId ? (
                          <ConnectorTrigger
                            to={makeUrl(routes.modal.connector, {
                              id: connectorAppId,
                            })}
                            title={authTooltip}
                          >
                            <ConnectorLogo
                              template={{
                                name: r.auth_connector,
                                identifier: r.auth_connector,
                              }}
                              size={12}
                            />
                          </ConnectorTrigger>
                        ) : (
                          <span
                            title={authTooltip}
                            style={{ display: "inline-flex" }}
                          >
                            <ConnectorLogo
                              template={{
                                name: r.auth_connector,
                                identifier: r.auth_connector,
                              }}
                              size={12}
                            />
                          </span>
                        )
                      ) : (
                        r.auth_applied && (
                          <span
                            title={authTooltip}
                            style={{ display: "inline-flex" }}
                          >
                            <Icon
                              name="shield-check"
                              size={12}
                              color="var(--green-800)"
                            />
                          </span>
                        )
                      )}
                    </TrailingInner>
                  </TrailingTd>
                </Tr>
                {isExpanded && (
                  <tr>
                    <DetailTd colSpan={6}>
                      <RequestDetail
                        data={r}
                        executionId={executionId}
                        cache={bodyCache}
                      />
                    </DetailTd>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </Table>
      <div ref={bottomRef} />
    </ScrollContainer>
  );
}
